var XMLElement = require('./XMLElement');
var XMLAttribute = require('./XMLAttribute');

/**
 * JSON representation of XML nodes. Each node has a tag an can have multiple {@link XMLAttribute} entries assigned.
 * It can also have multiple child {@link XMLNode} elements assigned. The node can contain a value.
 *
 * @param {object|string} oConfig - Object representation of the XMLNode containing at least the tag of the element.
 * @param {string} oConfig.tag - String representation of the XML tag.
 * @param {object[]} [oConfig.attributes] - Aggregation for adding {@link XMLAttributes} to the current node.
 * @param {object[]} [oConfig.properties] - Aggregation for adding {@link XMLNode} objects as child objects to this node
 * @param {string} [oConfig.value] - String value inside the XML tag.
 * @class
 * @module xml/XMLNode
 */
function XMLNode(oConfig) {
	var i, j, config;

	XMLElement.call(this, oConfig);

	/* Shorthand functionality to simply assign the XMLNode tag name instead of a configuration object */
	config = typeof oConfig === 'string' ? {tag: oConfig} : oConfig;

	Object.defineProperty(this, 'root', {value: !!config.root, writable: false});
	this.tag = typeof config.tag == 'string' ? config.tag : '';
	this.attributes = [];
	this.childs = [];

	/* Initialize the XML node by filling the aggregations if present */
	if (config.attributes) {
		for (i = 0; i < config.attributes.length; i++) {
			this.addAttribute(config.attributes[i]);
		}
	}

	if (config.properties) {
		for (j = 0; j < config.properties.length; j++) {
			this.addChild(config.properties[j]);
		}
	}
}

XMLNode.prototype = Object.create(XMLElement.prototype);
XMLNode.prototype.constructor = XMLNode;

XMLNode.processingInformation = '<?xml version="1.0" encoding="UTF-8" standalone="yes"?>';


/**
 * Adds the provided XMLAttribute object to the XMLNode.
 * If no XMLAttribute is provided it will create one instead.
 * The method returns the XMLAttribute for chaining.
 *
 * @function addAttribute
 * @param {Object} oConfig - XMLAttribute or configuration object that represents a XMLAttribute
 * @returns {Object} - An instance of XMLAttribute that was added to this node
 */
XMLNode.prototype.addAttribute = function(oConfig) {
	var attribute = oConfig instanceof XMLAttribute ? oConfig : new XMLAttribute(oConfig);

	this.attributes.push(attribute);

	if (oConfig.id) {
		Object.defineProperty(this, oConfig.id, {value: attribute, writable: false, configurable: true});
	}

	return attribute;
};


/**
 * Adds the provided XMLNode object as child to the current XMLNode.
 * If no XMLNode is provided it will create one instead.
 * The method returns the XMLNode for chaining.
 *
 * @function addChild
 * @param {Object} oConfig - XMLNode or configuration object that represents a XMLNode
 * @returns {object} - An instance of XMLNode that was added to this node
 */
XMLNode.prototype.addChild = function(oConfig) {

	/* Additional Attributes of the child node will be added in the Constructor */
	var node = oConfig instanceof XMLNode ? oConfig : new XMLNode(oConfig);

	this.childs.push(node);

	if (oConfig.id) {
		Object.defineProperty(this, oConfig.id, {value: node, writable: false, configurable: true});
	}

	return node;
};


/**
 * Removes the property from this object and also deletes the
 * referenced item within the child nodes or attributes
 * aggregation.
 *
 * The method returns the previously referenced object or null if
 * the property name is not present on this particular XMLNode or
 * if no item is referenced.
 *
 * Builtin properties of XMLNode cannot be removed with this
 * function. These are "root", "tag", "attributes", "childs",
 * "value".
 *
 * @param {string} sProperty - Name of the property that should be removed
 * @returns {XMLNode|XMLAttribute|null} - The referenced object that gets removed as well
 */
XMLNode.prototype.remove = function(sProperty) {
	var index, referencedElement, aAggregation;

	/* Only proceed if property is present */
	if (!(sProperty in this)) {
		return null;
	}

	referencedElement = this[sProperty];

	/* Check if referenced element is a XMLAttribute */
	if (referencedElement instanceof XMLAttribute) {
		aAggregation = this.attributes;
	} else if (referencedElement instanceof XMLNode && !(sProperty in new XMLNode(''))) {
		aAggregation = this.childs;
	} else {

		/* Early return if referenced element is neither an XMLAttribute nor XMLNode */
		return null;
	}

	index = aAggregation.indexOf(referencedElement);
	aAggregation.splice(index, 1); // It is ensured that index > -1 otherwise #addChild or #addAttribute fail
	delete this[sProperty];

	return referencedElement;
};

/**
 * Allows recursive serialization of the object and its descendants. If the XMLNode is marked as root node,
 * it will automatically add the XML processing information in front of the serialized representation.
 *
 * @function serialize
 * @returns {string} - String representation of the XMLNode.
 */
XMLNode.prototype.serialize = function() {
	var sSerialized, i, j;

	sSerialized = (this.root ? XMLNode.processingInformation : '') + '<' + this.tag;

	/* Insert attributes including namespace information */
	for (i = 0; i < this.attributes.length; i++) {
		sSerialized += this.attributes[i].serialize();
	}

	/* Insert child nodes and value */
	if (this.childs.length || this.value || typeof this.value === 'number') {
		sSerialized += '>';
		sSerialized = (' ' + sSerialized).substr(1);

		for (j = 0; j < this.childs.length; j++) {
			sSerialized += typeof this.childs[j] === 'string' ? this.childs[j] : this.childs[j].serialize();
		}
		sSerialized += this.xmlEncode(this.value);

		sSerialized += '</' + this.tag + '>';
	} else {

		/* Shorthand tag support */
		sSerialized += '/>';
		sSerialized = (' ' + sSerialized).substr(1);
	}

	return sSerialized;
};

module.exports = XMLNode;


//////////////////
// WEBPACK FOOTER
// ./core/xlsx/src/xml/XMLNode.js
// module id = 0
// module chunks = 0